import { ChargeDescriptionMaster } from '../cdm'

export enum EstimateChargeDiscountType {
  Percentage = 'percentage',
  Fixed = 'fixed',
}

export interface EstimateCharge {
  type: ChargeDescriptionMaster.TYPE
  chargeId?: string

  /**
   * Billable unit amount before negotiations
   */
  unitCharge: number
  chargeCode: string
  /** Canonical procedure code from the charge master: CPT, else HCPCS, else the charge code. */
  code?: string
  description?: string
  revCode?: string
  cptCode?: string
  hcpcsCode?: string
  modifiers?: string[]
  quantity: number

  /**
   * The type of CPT-level discount: percentage or fixed amount
   */
  cptDiscountType?: EstimateChargeDiscountType

  /**
   * The discount value configured for this charge.
   * If type is 'percentage', this is the percent (e.g., 10 for 10%).
   * If type is 'fixed', this is the dollar amount.
   */
  cptDiscountAmount?: number

  /**
   * The number of units the discount applies to.
   * If not set, the discount applies to all units (quantity).
   */
  cptDiscountedUnits?: number

  /**
   * The calculated discount amount applied to this charge for a single visit.
   * This is the result of dividing the total discount by the number of visits
   * the charge appears in. Populated during estimate calculation.
   */
  cptDiscountApplied?: number

  amount?: number
  expectedAmount?: number

  /** Extended amount for the line. Mirrors `totalCharge` on the API's `EstimateCharge`. */
  totalCharge?: number

  /**
   * Line amount (all units) negotiated with insurance, needed to be paid by insurance and patient
   * Comes from pricer
   */
  negotiatedAmount?: number

  currency?: string
  resourceId?: string
  resourceTaxId?: string
  resourceNPI?: string
  facilityId?: string
  appointmentIds?: string[]
  visitNumbers?: number[]
  /**
   * Set when the pricer returned an error for this charge's visit grouping.
   * Indicates the charge amount may be inaccurate.
   */
  pricerError?: string
}

/**
 * Modifiers derived from a charge code only seed charges the estimate has not seen before. An
 * existing row keeps whatever the user left on it, including a deliberately cleared value.
 */
export function resolveChargeModifiers(
  existing: Pick<EstimateCharge, 'modifiers'> | undefined,
  derived: string[] | undefined,
): string[] | undefined {
  return existing ? existing.modifiers : derived
}

/**
 * Product-configured modifiers are set by the clinic and win; the charge-code split only fills
 * the gap for charges whose product carries none.
 */
export function seedChargeModifiers(
  productModifiers: string[] | undefined,
  derived: string[] | undefined,
): string[] | undefined {
  return productModifiers?.length ? productModifiers : derived
}
